// @flow
import useSelectPagination from 'components/SendResource/hooks/useSelectPagination'
import Spinner from 'components/Spinner'
import { useLoadPackets } from 'queries/packets/useLoadPackets'
import * as React from 'react'
import BaseField from 'ui/molecules/BaseField'
import Select from 'ui/Select'

type PacketOption = {
  value: string,
  label: string,
  hasOfficeUseQuestions: boolean,
}

type Props = {
  onChange: (value: void | PacketOption | Array<PacketOption>) => void,
  value: void | PacketOption | Array<PacketOption>,
  isMulti?: boolean,
  disabled?: boolean,

  compact?: boolean,
  horizontal?: boolean,
  label?: ?string,
  reverse?: boolean,
  smallLabel?: boolean,
  semibold?: boolean,
  hasError?: boolean,
  errorLabel?: ?string,
}

function PacketSelectField({
  disabled,
  label = 'Select Packet',

  compact,
  horizontal,
  reverse,
  smallLabel,
  semibold,
  hasError,
  errorLabel,

  ...props
}: Props): React.Node {
  const [hasMore, setHasMore] = React.useState(false)
  const { params, onInputChange, onMenuScrollToBottom, setOptions, options } =
    useSelectPagination(hasMore)
  const { data, isLoading, isFetching } = useLoadPackets(params, {
    keepPreviousData: true,
  })

  React.useEffect(() => {
    setHasMore(Boolean(data?.next))
  }, [data?.next])

  React.useEffect(() => {
    const newOptions =
      data?.results
        .filter((p) => p.enabled)
        .map((o) => ({
          value: o.id,
          label: o.name,
          hasOfficeUseQuestions: o.office_use_forms_count > 0,
        }))
        .sort((a, b) => a.label?.localeCompare(b.label)) || []

    setOptions((options) => [...options, ...newOptions])
  }, [data?.results, setOptions])

  const dropdownLabel = React.useMemo(
    () => (
      <div className="flex items-center">
        {isLoading && <Spinner color="onLight" size={20} />}
        <span className="flex-1">{label}</span>
      </div>
    ),
    [isLoading, label]
  )

  const dropdownPlaceholder = data?.results?.length
    ? '-- Select Packet --'
    : 'No Packet Available'

  return (
    <BaseField
      compact={compact}
      errorLabel={errorLabel}
      hasError={hasError}
      horizontal={horizontal}
      label={dropdownLabel}
      reverse={reverse}
      semibold={semibold}
      smallLabel={smallLabel}
    >
      {/* $FlowFixMe */}
      <Select
        showOptions
        disabled={isLoading || disabled}
        isClearable={false}
        isFetching={isFetching}
        loading={isLoading}
        onMenuScrollToBottom={onMenuScrollToBottom}
        onType={onInputChange}
        options={options}
        placeholder={dropdownPlaceholder}
        {...props}
      />
    </BaseField>
  )
}

export default PacketSelectField
